import {
  Toolbar,
  useMediaQuery,
  useTheme,
  type Theme,
  Box,
  Button,
  Popper,
  Paper,
  ClickAwayListener,
  Fade,
  Stack,
  Link,
} from '@mui/material';
import { LOCAL_LINKS_HEIGHT } from '../styles';
import { useState, useRef } from 'react';
import { Text } from '../../Text';

export interface LocalLinksProps {
  linkData: (LocalLinkData | MenuLinkData)[];
  linkComponent: React.ElementType;
}

export interface LocalLinkData {
  text: string;
  href: string;
  subOptions?: LocalLinkData[];
}

export interface MenuLinkData {
  text: string;
  options: LocalLinkData[];
}

export const LocalLinks = ({ linkData, linkComponent }: LocalLinksProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery((mediaTheme: Theme) => mediaTheme.breakpoints.down(720));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuOptions, setMenuOptions] = useState<LocalLinkData[]>([]);
  const [subMenuAnchorEl, setSubMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [subMenuOptions, setSubMenuOptions] = useState<LocalLinkData[]>([]);

  // Single timeout to control both main and sub-menu
  const closeMenuTimeoutId = useRef<NodeJS.Timeout | null>(null);

  const handleSubMenuOpen = (
    event: React.MouseEvent<HTMLDivElement>,
    subOptions?: LocalLinkData[],
  ) => {
    setSubMenuAnchorEl(event.currentTarget);
    if (subOptions) {
      setSubMenuOptions(subOptions);
    } else {
      setSubMenuOptions([]);
    }
  };

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>, options?: LocalLinkData[]) => {
    setAnchorEl(event.currentTarget);
    if (options) {
      setMenuOptions(options);
    } else {
      setMenuOptions([]);
    }
  };

  // Close the main menu and sub-menu
  const handleClose = () => {
    closeMenuTimeoutId.current = setTimeout(() => {
      setAnchorEl(null);
      setSubMenuAnchorEl(null);
    }, 200); // Slight delay to allow for smooth hover transitions
  };

  // Cancel closing if the user hovers over either menu or sub-menu
  const cancelClose = () => {
    if (closeMenuTimeoutId.current) {
      clearTimeout(closeMenuTimeoutId.current);
    }
  };

  return (
    <Toolbar
      data-component="LocalLinks"
      sx={{
        width: '100%',
        height: `${LOCAL_LINKS_HEIGHT}px`,
        minHeight: `${LOCAL_LINKS_HEIGHT}px!important`,
        backgroundColor: theme.palette.black.main,
        display: isSmallScreen ? 'none' : 'flex',
        justifyContent: 'center',
        boxSizing: 'border-box',
        zIndex: 1100,
      }}
    >
      <Stack justifyContent="center" alignItems="center" direction="row" width="100%" height="100%">
        {linkData.map((link) => {
          if ('options' in link) {
            return (
              <Box
                key={link.text}
                sx={{
                  maxWidth: '150px',
                  flexGrow: 1,
                  height: '100%',
                  textAlign: 'center',
                  '&:hover .local-links-label': {
                    borderBottomColor: theme.palette.white.main,
                  },
                }}
              >
                <Button
                  sx={{
                    padding: 0,
                    textTransform: 'none',
                    minWidth: 0,
                    height: '100%',
                    zIndex: theme.zIndex.modal + 1,
                  }}
                  onMouseOver={(e) => handleOpen(e, link.options)}
                >
                  <Text
                    className="local-links-label"
                    variant="p"
                    color="white"
                    sx={{
                      margin: 0,
                      paddingBottom: '2px',
                      borderBottom: '2px solid transparent',
                      transition: 'border-color .15s ease-out',
                    }}
                  >
                    {link.text}
                  </Text>
                </Button>
                {/* Main menu link */}
                <MenuLink
                  anchorEl={anchorEl}
                  options={menuOptions}
                  onClose={handleClose}
                  onOptionHover={handleSubMenuOpen}
                  cancelClose={cancelClose}
                />
                {/* Sub-menu link */}
                <MenuLink
                  anchorEl={subMenuAnchorEl}
                  options={subMenuOptions}
                  onClose={handleClose}
                  cancelClose={cancelClose}
                  placement="right-start"
                />
              </Box>
            );
          } else {
            return (
              <Link
                key={link.text}
                component={linkComponent}
                to={link.href}
                sx={{
                  maxWidth: '150px',
                  flexGrow: 1,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  '&:hover .local-links-label': {
                    borderBottomColor: theme.palette.white.main,
                  },
                }}
                underline="none"
                data-component="LocalLinksLinkDesktop"
              >
                <Text
                  className="local-links-label"
                  variant="p"
                  fontSize="1rem"
                  color="silver"
                  sx={{
                    margin: 0,
                    paddingBottom: '2px',
                    borderBottom: '2px solid transparent',
                    transition: 'border-color .15s ease-out',
                  }}
                >
                  {link.text}
                </Text>
              </Link>
            );
          }
        })}
      </Stack>
    </Toolbar>
  );
};

const MenuLink = ({
  anchorEl,
  options,
  onClose,
  onOptionHover,
  cancelClose,
  placement = 'bottom-start',
}: {
  anchorEl: HTMLElement | null;
  options: LocalLinkData[];
  onClose: () => void;
  onOptionHover?: (event: React.MouseEvent<HTMLDivElement>, options?: LocalLinkData[]) => void;
  cancelClose?: () => void;
  placement?: 'bottom-start' | 'right-start';
}) => {
  const theme = useTheme();

  return (
    <Popper
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      placement={placement}
      transition
      disablePortal
      onMouseEnter={cancelClose} // Prevent closing while hovering
      onMouseLeave={onClose} // Close when mouse leaves both main and sub-menu
      sx={{ zIndex: theme.zIndex.modal - 1 }}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={350}>
          <div>
            <ClickAwayListener onClickAway={onClose}>
              <Paper sx={{ maxWidth: '200px', backgroundColor: theme.palette.white.main }}>
                <Box>
                  {options.map((option) => (
                    <Box
                      key={option.href}
                      onMouseOver={(e) => {
                        if (onOptionHover) {
                          onOptionHover(e, 'subOptions' in option ? option.subOptions : undefined);
                        }
                      }}
                      onClick={onClose}
                      sx={{
                        padding: '8px',
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: theme.palette.grey[200],
                        },
                      }}
                    >
                      <Link href={option.href} underline="none">
                        <Text variant="p" color="black">
                          {option.text}
                        </Text>
                      </Link>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </ClickAwayListener>
          </div>
        </Fade>
      )}
    </Popper>
  );
};
